import type { DocumentPickerAsset } from "expo-document-picker";
import type { ImagePickerAsset } from "expo-image-picker";
import { useEffect, useRef, useState } from "react";
import { Platform } from "react-native";
import type { TamaguiElement } from "tamagui";

export default function useFileDropZone({
  enabled = true,
  onFiles,
}: {
  enabled?: boolean;
  onFiles: (
    assets: (DocumentPickerAsset | ImagePickerAsset)[],
    files: File[],
  ) => void;
}) {
  const ref = useRef<TamaguiElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (Platform.OS !== "web" || !enabled) return;
    const el = ref.current as unknown as HTMLElement | null;
    if (!el) return;

    const stop = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onDragOver = (e: Event) => {
      stop(e);
      setIsDragging(true);
    };
    const onDragLeave = (e: Event) => {
      stop(e);
      setIsDragging(false);
    };
    const onDrop = (e: Event) => {
      stop(e);
      setIsDragging(false);
      const files = Array.from((e as DragEvent).dataTransfer?.files ?? []);
      if (files.length === 0) return;
      const assets = files.map((file) => ({
        uri: URL.createObjectURL(file),
        mimeType: file.type,
        name: file.name,
        size: file.size,
      })) as unknown as DocumentPickerAsset[];
      onFiles(assets, files);
    };

    el.addEventListener("dragenter", onDragOver);
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragenter", onDragOver);
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [enabled, onFiles]);

  return { ref, isDragging };
}
